import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_API_URL ||
  "https://porfolio-backend-production-aab2.up.railway.app/api";

const STATUS_INFO = {
  Pending:   { color: "#f7c948", bg: "#2b1a00", text: "Your payment is being verified. Please wait 24 hours." },
  Active:    { color: "#2cb67d", bg: "#0d2b1d", text: "Your internship is active! Complete your daily tasks." },
  Completed: { color: "#00c6ff", bg: "#0d1a2b", text: "Congratulations! You have completed the internship." },
  Revoked:   { color: "#ff4444", bg: "#2b0d0d", text: "Your registration was revoked. Contact support." },
};

const CERT_INFO = {
  "Not Issued": { color: "#888",    icon: "⏳", text: "Certificate will be issued after completion." },
  Valid:        { color: "#2cb67d", icon: "✅", text: "Your certificate is ready! Download it below." },
  Revoked:      { color: "#ff4444", icon: "❌", text: "Certificate has been revoked. Contact support." },
};

const SUB_COLORS = {
  Submitted: { color: "#f7c948", bg: "#2b1a00" },
  Approved:  { color: "#2cb67d", bg: "#0d2b1d" },
  Rejected:  { color: "#ff4444", bg: "#2b0d0d" },
};

export default function StudentPortal() {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [submitForm, setSubmitForm] = useState({ taskId: "", link: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState("");

  const token = localStorage.getItem("studentToken");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  useEffect(() => {
    if (!token) { navigate("/student/login"); return; }
    axios.get(`${API}/students/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setStudent(res.data))
      .catch(() => {
        localStorage.removeItem("studentToken");
        navigate("/student/login");
      })
      .finally(() => setLoading(false));
  }, [navigate, token]);

  useEffect(() => {
    if (tab === "tasks" && token) {
      setTasksLoading(true);
      axios.get(`${API}/tasks/student`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setTasks(res.data))
        .catch(() => showToast("Failed to load tasks."))
        .finally(() => setTasksLoading(false));
    }
  }, [tab, token]);

  async function handleSubmit(taskId) {
    if (!submitForm.link.trim()) { showToast("Please enter a submission link."); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/tasks/submit`,
        {
          taskId,
          submissionLink: submitForm.link,
          submissionUrl: submitForm.link,
          notes: submitForm.notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast("Task submitted successfully!");
      setSubmitForm({ taskId: "", link: "", notes: "" });
      // Refresh tasks
      const res = await axios.get(`${API}/tasks/student`, { headers: { Authorization: `Bearer ${token}` } });
      setTasks(res.data);
    } catch (err) {
      showToast(err.response?.data?.message || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleLogout() {
    localStorage.removeItem("studentToken");
    localStorage.removeItem("studentData");
    navigate("/student/login");
  }

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="spinner" />
    </div>
  );

  if (!student) return null;

  const statusInfo = STATUS_INFO[student.status] || STATUS_INFO.Pending;
  const certInfo = CERT_INFO[student.certificateStatus] || CERT_INFO["Not Issued"];

  return (
    <div style={{ paddingTop: 80, paddingBottom: 80, minHeight: "100vh" }}>
      <div className="container" style={{ maxWidth: 800 }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
          <div>
            <p style={{ color: "#555", fontSize: 13, marginBottom: 4 }}>Welcome back,</p>
            <h1 style={{ fontSize: 26, fontWeight: 800 }}>{student.fullName} 👋</h1>
            <p style={{ color: "#555", fontSize: 13, marginTop: 4 }}>{student.domain}</p>
          </div>
          <button className="btn btn-outline" onClick={handleLogout} style={{ fontSize: 13 }}>Logout</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 28, borderBottom: "1px solid #1e1e2e" }}>
          {[
            { key: "overview", label: "📊 Overview" },
            { key: "tasks", label: "📋 Tasks" },
            { key: "certificate", label: "🎓 Certificate" },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              background: "none", border: "none",
              borderBottom: tab === t.key ? "2px solid #7f5af0" : "2px solid transparent",
              color: tab === t.key ? "#7f5af0" : "#888",
              fontWeight: 600, fontSize: 13,
              padding: "10px 16px", cursor: "pointer", marginBottom: -1,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === "overview" && (
          <>
            <div className="card" style={{ marginBottom: 16, borderColor: statusInfo.color }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", color: "#555" }}>Status</span>
                <span style={{ fontSize: 12, padding: "3px 12px", borderRadius: 100, fontWeight: 700, background: statusInfo.bg, color: statusInfo.color }}>{student.status}</span>
              </div>
              <p style={{ color: "#888", fontSize: 14, margin: 0 }}>{statusInfo.text}</p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
              <InfoCard label="Domain" value={student.domain} icon="🎯" />
              <InfoCard label="College" value={student.collegeName} icon="🏫" />
              <InfoCard label="Certificate ID" value={student.certificateId} icon="🔏" mono />
              <InfoCard label="Registered" value={formatDate(student.createdAt)} icon="📅" />
            </div>

            <div className="card">
              <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: 15 }}>📋 Program Progress</h3>
              {[
                { step: "1", label: "Registration & Payment", done: true },
                { step: "2", label: "Payment Verification (24hrs)", done: student.status !== "Pending" },
                { step: "3", label: "Internship Tasks", done: student.status === "Completed" },
                { step: "4", label: "Certificate Issued", done: student.certificateStatus === "Valid" },
              ].map((s) => (
                <div key={s.step} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700,
                    background: s.done ? "#0d2b1d" : "#1a1a2e",
                    color: s.done ? "#2cb67d" : "#444",
                    border: `1px solid ${s.done ? "#2cb67d" : "#2a2a3e"}`,
                  }}>
                    {s.done ? "✓" : s.step}
                  </div>
                  <span style={{ fontSize: 14, color: s.done ? "#e8e8f0" : "#555" }}>{s.label}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Tasks Tab */}
        {tab === "tasks" && (
          <>
            {student.status === "Pending" ? (
              <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
                <p style={{ fontSize: 32, marginBottom: 12 }}>⏳</p>
                <p style={{ color: "#888" }}>Tasks will be available once your payment is verified.</p>
              </div>
            ) : tasksLoading ? (
              <div className="spinner" />
            ) : tasks.length === 0 ? (
              <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
                <p style={{ fontSize: 32, marginBottom: 12 }}>📋</p>
                <p style={{ color: "#888" }}>No tasks assigned yet for your domain ({student.domain}).</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {tasks.map((task) => (
                  <div key={task._id} className="card">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ background: "#1a1a2e", color: "#7f5af0", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 100 }}>
                          Task {task.day}
                        </span>
                        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{task.title}</h3>
                      </div>
                      {task.submission && (
                        <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 100, fontWeight: 700, background: SUB_COLORS[task.submission.status]?.bg, color: SUB_COLORS[task.submission.status]?.color }}>
                          {task.submission.status}
                        </span>
                      )}
                    </div>

                    <p style={{ color: "#888", fontSize: 14, marginBottom: 12, lineHeight: 1.6 }}>{task.description}</p>

                    {task.resources && (
                      <p style={{ fontSize: 12, color: "#555", marginBottom: 12 }}>
                        📎 Resources:
                        {(Array.isArray(task.resources)
                          ? task.resources
                          : String(task.resources).split(",")
                        )
                          .map((r) => r.trim())
                          .filter(Boolean)
                          .map((r, i) => (
                            <a
                              key={i}
                              href={r}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: "#7f5af0", marginLeft: 8, display: "inline-block" }}
                            >
                              Link {i + 1}
                            </a>
                          ))}
                      </p>
                    )}

                    {task.submission?.adminFeedback && (
                      <div style={{ background: "#0f0f1a", border: "1px solid #2a2a3e", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#aaa" }}>
                        💬 Feedback: {task.submission.adminFeedback}
                      </div>
                    )}

                    {/* Submit form */}
                    {(!task.submission || task.submission.status === "Rejected") && (
                      <div style={{ borderTop: "1px solid #1e1e2e", paddingTop: 12 }}>
                        {submitForm.taskId === task._id ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            <input
                              type="url"
                              placeholder="GitHub / Google Drive / any link"
                              value={submitForm.link}
                              onChange={(e) => setSubmitForm({ ...submitForm, link: e.target.value })}
                              style={{ fontSize: 13 }}
                            />
                            <input
                              type="text"
                              placeholder="Notes (optional)"
                              value={submitForm.notes}
                              onChange={(e) => setSubmitForm({ ...submitForm, notes: e.target.value })}
                              style={{ fontSize: 13 }}
                            />
                            <div style={{ display: "flex", gap: 8 }}>
                              <button className="btn btn-primary" style={{ fontSize: 13, padding: "8px 20px" }} onClick={() => handleSubmit(task._id)} disabled={submitting}>
                                {submitting ? "Submitting..." : "Submit Task"}
                              </button>
                              <button className="btn btn-outline" style={{ fontSize: 13, padding: "8px 16px" }} onClick={() => setSubmitForm({ taskId: "", link: "", notes: "" })}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: 13, padding: "8px 20px" }}
                            onClick={() => setSubmitForm({ taskId: task._id, link: "", notes: "" })}
                          >
                            {task.submission?.status === "Rejected" ? "Resubmit Task" : "Submit Task →"}
                          </button>
                        )}
                      </div>
                    )}

                    {task.submission?.status === "Approved" && (
                      <div style={{ borderTop: "1px solid #1e1e2e", paddingTop: 12, fontSize: 13, color: "#2cb67d" }}>
                        ✅ Submitted: <a href={task.submission.submissionUrl || task.submission.submissionLink} target="_blank" rel="noreferrer" style={{ color: "#7f5af0" }}>View submission →</a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Certificate Tab */}
        {tab === "certificate" && (
          <div className="card" style={{ borderColor: certInfo.color }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 28 }}>{certInfo.icon}</span>
              <div>
                <div style={{ fontSize: 11, color: "#555", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 4 }}>Certificate Status</div>
                <span style={{ fontSize: 13, padding: "4px 14px", borderRadius: 100, fontWeight: 700, color: certInfo.color, border: `1px solid ${certInfo.color}` }}>
                  {student.certificateStatus}
                </span>
              </div>
            </div>
            <p style={{ color: "#888", fontSize: 14, marginBottom: 16 }}>{certInfo.text}</p>
            {student.issuedAt && (
              <p style={{ color: "#555", fontSize: 13, marginBottom: 16 }}>Issued on {formatDate(student.issuedAt)}</p>
            )}
            <div style={{ background: "#0f0f1a", border: "1px solid #1e1e2e", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Certificate ID</div>
              <div style={{ fontFamily: "monospace", letterSpacing: 2, color: "#00c6ff", fontSize: 15 }}>{student.certificateId}</div>
            </div>
            {student.certificateStatus === "Valid" && (
              <button
                className="btn btn-primary"
                style={{ width: "100%", padding: "12px", fontSize: 14 }}
                onClick={() => navigate(`/verify?id=${student.certificateId}`)}
              >
                ⬇ View & Download Certificate
              </button>
            )}
          </div>
        )}

        <p style={{ textAlign: "center", color: "#333", fontSize: 13, marginTop: 28 }}>
          Need help? <a href="mailto:support@ariyomtechsolutions.site" style={{ color: "#7f5af0" }}>support@ariyomtechsolutions.site</a>
        </p>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function InfoCard({ label, value, icon, mono }) {
  return (
    <div className="card" style={{ padding: "16px 20px" }}>
      <div style={{ fontSize: 11, color: "#555", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>{icon} {label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#c0c0e0", fontFamily: mono ? "monospace" : "inherit", letterSpacing: mono ? 1 : 0 }}>
        {value || "—"}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
}